import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

/** Minimal SVG sparkline with area fill. */
export function Sparkline({
  data,
  className,
  stroke = "#00E5FF",
  height = 64,
  testId,
}: {
  data: number[];
  className?: string;
  stroke?: string;
  height?: number;
  testId?: string;
}) {
  const reduced = useReducedMotion();
  const w = 300;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => [(i / (data.length - 1)) * w, height - 6 - ((v - min) / range) * (height - 14)]);
  const line = pts.map((p) => p.join(",")).join(" ");
  const area = `0,${height} ${line} ${w},${height}`;
  const gid = `spark-${stroke.replace("#", "")}`;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className={cn("w-full", className)} preserveAspectRatio="none" data-testid={testId} role="img" aria-label="trend chart">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.28" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${gid})`} />
      <motion.polyline
        points={line}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        initial={reduced ? undefined : { pathLength: 0 }}
        animate={reduced ? undefined : { pathLength: 1 }}
        transition={{ duration: 1.6, ease: "easeOut" }}
      />
      {pts.length > 0 && <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.5" fill={stroke} />}
    </svg>
  );
}

/** Vertical bar history. */
export function BarHistory({ data, className, testId }: { data: number[]; className?: string; testId?: string }) {
  const reduced = useReducedMotion();
  const max = Math.max(...data) || 1;
  return (
    <div className={cn("flex h-24 items-end gap-1.5", className)} data-testid={testId}>
      {data.map((v, i) => (
        <motion.div
          key={i}
          className="flex-1 bg-gradient-to-t from-blue/40 to-cyan/80"
          initial={reduced ? undefined : { height: 0 }}
          animate={reduced ? undefined : { height: `${(v / max) * 100}%` }}
          transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
          style={reduced ? { height: `${(v / max) * 100}%` } : undefined}
          title={`${v}`}
        />
      ))}
    </div>
  );
}

/** Horizontal consistency bars. */
export function ConsistencyBars({ data }: { data: { label: string; value: number }[] }) {
  const reduced = useReducedMotion();
  return (
    <div className="space-y-3">
      {data.map((d, i) => (
        <div key={d.label} className="flex items-center gap-3">
          <span className="w-24 shrink-0 font-mono text-[10px] uppercase tracking-[0.18em] text-dim">{d.label}</span>
          <div className="h-1 flex-1 bg-navy/80">
            <motion.div
              className="h-full bg-cyan"
              initial={reduced ? undefined : { width: 0 }}
              whileInView={{ width: `${d.value}%` }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              style={reduced ? { width: `${d.value}%` } : undefined}
            />
          </div>
          <span className="w-10 text-right font-mono text-[10px] text-cyan">{d.value}</span>
        </div>
      ))}
    </div>
  );
}

/** Radial consequence score gauge. */
export function RadialScore({ score, testId }: { score: number; testId?: string }) {
  const reduced = useReducedMotion();
  const r = 70;
  const c = 2 * Math.PI * r;
  const pct = score / 100;
  return (
    <div className="relative h-48 w-48" data-testid={testId}>
      <svg viewBox="0 0 176 176" className="h-full w-full -rotate-90">
        <circle cx="88" cy="88" r={r} fill="none" stroke="#071A35" strokeWidth="6" />
        <defs>
          <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0066FF" />
            <stop offset="100%" stopColor="#00E5FF" />
          </linearGradient>
        </defs>
        <motion.circle
          cx="88"
          cy="88"
          r={r}
          fill="none"
          stroke="url(#scoreGrad)"
          strokeWidth="6"
          strokeLinecap="butt"
          strokeDasharray={c}
          initial={reduced ? undefined : { strokeDashoffset: c }}
          whileInView={{ strokeDashoffset: c * (1 - pct) }}
          viewport={{ once: true }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
          style={reduced ? { strokeDashoffset: c * (1 - pct) } : undefined}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-4xl font-bold text-ink">{score}</span>
        <span className="mono-label mt-1 text-[9px]">/ 100</span>
      </div>
    </div>
  );
}
